import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useProject } from '../context';
import {
  createPendingAssistantMessage,
  resolveTestBotTypingDelayChannel,
  resolveTestBotTypingDelayMs,
} from './testBotTypingDelay';

// Attachment types
interface PendingAttachment {
  file: File;
  preview: string | null;
  base64Data: string;
}

// Maximum file size (10MB)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Allowed MIME types
const ALLOWED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm',
  'video/mp4', 'video/webm', 'video/ogg',
  'application/pdf', 'text/plain', 'application/json',
];

/**
 * Available TTS voices for OpenAI.
 */
const TTS_VOICES = [
  { value: 'alloy', label: 'Alloy', description: 'Neutral and balanced' },
  { value: 'echo', label: 'Echo', description: 'Warm and conversational' },
  { value: 'fable', label: 'Fable', description: 'British and expressive' },
  { value: 'onyx', label: 'Onyx', description: 'Deep and authoritative' },
  { value: 'nova', label: 'Nova', description: 'Friendly and upbeat' },
  { value: 'shimmer', label: 'Shimmer', description: 'Clear and optimistic' },
] as const;

type TTSVoiceId = typeof TTS_VOICES[number]['value'];

/**
 * Test session from the database
 */
interface TestSession {
  id: string;
  name: string;
  userId: string;
  platform: string | null;
  status: string | null;
  lastActivityAt: string | null;
  createdAt: string | null;
}

/**
 * Attachment stored with a message
 */
interface MessageAttachment {
  type: 'image' | 'document';
  url?: string;
  data?: string; // base64 data for locally added attachments
  mimeType: string;
  filename?: string;
}

/**
 * Message in the conversation
 */
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  showTypingIndicator?: boolean;
  isHidden?: boolean;
  /** Message type from database (text, tool_context, voice, etc.) */
  messageType?: string;
  /** Context data from database */
  context?: Record<string, unknown>;
  /** Attachments for this message */
  attachments?: MessageAttachment[];
  /** Base64-encoded audio data for voice responses */
  audioData?: string;
  /** Audio content type (e.g., 'audio/mpeg') */
  audioContentType?: string;
}

/**
 * Tool execution info from the backend
 */
interface ToolExecutionInfo {
  toolName: string;
  durationMs: number;
  success: boolean;
}

/**
 * Tool analytics breakdown by tool
 */
interface ToolBreakdown {
  toolId: string;
  toolName: string;
  executions: number;
  successes: number;
  failures: number;
  avgLatencyMs: number | null;
  successRate: number;
}

/**
 * Session tool analytics
 */
interface SessionToolAnalytics {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  avgLatencyMs: number | null;
  toolBreakdown: ToolBreakdown[];
  recentExecutions: {
    toolId: string | null;
    toolName: string | null;
    durationMs: number | null;
    success: boolean;
    createdAt: string | null;
  }[];
}

/**
 * Debug information for the last response
 */
interface DebugInfo {
  model: string;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  latency: number;
  rawResponse: Record<string, unknown>;
  /** Tools executed in the last response */
  toolsExecuted?: ToolExecutionInfo[];
  /** ID of the hidden tool context message */
  toolContextMessageId?: string;
}

/**
 * Streaming subscription input state (legacy - for old test endpoint)
 */
interface StreamingState {
  botId: string;
  message: string;
  history: Array<{ role: 'user' | 'assistant'; content: string }>;
  startTime: number;
  messageId: string;
}

/**
 * Attachment input for API calls
 */
interface AttachmentInput {
  type: 'image' | 'document';
  data: string;
  mimeType: string;
  filename?: string;
}

/**
 * Session streaming state (for new test session endpoint)
 */
interface SessionStreamingState {
  sessionId: string;
  message: string;
  startTime: number;
  messageId: string;
  attachments?: AttachmentInput[];
  responseFormat?: 'text' | 'voice';
  voiceId?: TTSVoiceId;
}

/**
 * Simulation state tracking for auto-reply loop
 */
type SimulationTurnState = 'idle' | 'waiting_for_bot' | 'waiting_for_delay' | 'generating_user';

/**
 * Test Bot page component.
 *
 * A conversational chat interface for testing bot interactions.
 * Maintains conversation history and displays debug information in a side panel.
 * Features real-time streaming of bot responses for a more interactive experience.
 */
export function TestBotPage() {
  const { currentBotId: botId, currentBot, isLoading: contextLoading } = useProject();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [streamingState, setStreamingState] = useState<StreamingState | null>(null);
  const [streamedContent, setStreamedContent] = useState('');
  const [botName, setBotName] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Session management state
  const [sessions, setSessions] = useState<TestSession[]>([]);
  const [currentSession, setCurrentSession] = useState<TestSession | null>(null);
  const [sessionStreamingState, setSessionStreamingState] = useState<SessionStreamingState | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [showSessionList, setShowSessionList] = useState(false);
  const [showHiddenMessages, setShowHiddenMessages] = useState(true);
  const [sessionToolAnalytics, setSessionToolAnalytics] = useState<SessionToolAnalytics | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  // Simulation state
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationPrompt, setSimulationPrompt] = useState('');
  const [simulationContext, setSimulationContext] = useState('');
  const [simulationDelay, setSimulationDelay] = useState(10000); // milliseconds (10 seconds default)
  const [simulationTurnState, setSimulationTurnState] = useState<SimulationTurnState>('idle');
  const simulationDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Attachment state
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Voice response toggle state
  const [nextResponseFormat, setNextResponseFormat] = useState<'text' | 'voice'>('text');
  // Selected TTS voice for voice responses
  const [selectedVoiceId, setSelectedVoiceId] = useState<TTSVoiceId>('alloy');
  // Track the message ID of the latest voice response for auto-play
  const [pendingAutoPlayMessageId, setPendingAutoPlayMessageId] = useState<string | null>(null);
  // Track the message ID awaiting auto-play (waits for UI to settle before triggering actual auto-play)
  const [awaitingAutoPlayMessageId, setAwaitingAutoPlayMessageId] = useState<string | null>(null);
  // Track message IDs that have started auto-playing (to prevent key changes during playback)
  const [autoPlayingMessageIds, setAutoPlayingMessageIds] = useState<Set<string>>(new Set());
  const typingIndicatorTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clearTypingIndicatorTimer = useCallback((messageId: string) => {
    const timer = typingIndicatorTimersRef.current.get(messageId);
    if (!timer) return;
    clearTimeout(timer);
    typingIndicatorTimersRef.current.delete(messageId);
  }, []);

  // Fetch full bot details to get the system prompt
  const botDetailsQuery = trpc.bots.get.useQuery(
    { id: botId ?? '' },
    { enabled: !!botId }
  );
  const botDetails = botDetailsQuery.data;

  // Mutation for updating bot (to save simulation settings)
  const updateBotMutation = trpc.bots.update.useMutation();

  // Mutation for generating simulated user response
  const generateSimulatedUserMutation = trpc.bots.generateSimulatedUserResponse.useMutation();

  // Test sessions queries and mutations
  const sessionsQuery = trpc.testSessions.list.useQuery(
    { botId: botId ?? '', limit: 20 },
    { enabled: !!botId }
  );

  const createSessionMutation = trpc.testSessions.create.useMutation({
    onSuccess: (newSession: { id: string; name: string; userId: string; platform: string | null; status: string; createdAt: string }) => {
      // Refresh sessions list
      sessionsQuery.refetch();
      // Select the new session
      setCurrentSession({
        id: newSession.id,
        name: newSession.name,
        userId: newSession.userId,
        platform: newSession.platform,
        status: newSession.status,
        lastActivityAt: null,
        createdAt: newSession.createdAt,
      });
      setMessages([]);
      setIsCreatingSession(false);
      setNewSessionName('');
    },
  });

  const getSessionQuery = trpc.testSessions.get.useQuery(
    { sessionId: currentSession?.id ?? '' },
    { enabled: !!currentSession?.id }
  );
  const activeSessionPlatform = getSessionQuery.data?.platform
    ?? currentSession?.platform
    ?? getSessionQuery.data?.messages
      ?.slice()
      .reverse()
      .find((message: { platform: string | null }) => !!message.platform)
      ?.platform
    ?? null;
  const activeTestChannel = resolveTestBotTypingDelayChannel(!!currentSession, activeSessionPlatform);
  const typingDelayMs = resolveTestBotTypingDelayMs(botDetails, activeTestChannel);
  const isTypingDelaySettingsPending = activeTestChannel === 'whatsapp'
    && !!botId
    && botDetailsQuery.isFetching
    && !botDetails;

  const archiveSessionMutation = trpc.testSessions.archive.useMutation({
    onSuccess: () => {
      sessionsQuery.refetch();
      if (currentSession) {
        setCurrentSession(null);
        setMessages([]);
      }
    },
  });

  // Mutation for updating response format preference
  const updateResponseFormatMutation = trpc.testSessions.updateResponseFormat.useMutation();

  // Subscribe to new messages (e.g., scheduled messages from worker) for the current session
  trpc.testSessions.newMessages.useSubscription(
    { sessionId: currentSession?.id ?? '' },
    {
      enabled: !!currentSession?.id,
      onData: (newMessage: {
        id: string;
        direction: string | null;
        messageType: string | null;
        content: string | null;
        createdAt: string | null;
      }) => {
        // Check if this message already exists (to avoid duplicates)
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === newMessage.id);
          if (exists) return prev;

          // Add the new message
          const chatMessage: ChatMessage = {
            id: newMessage.id,
            role: newMessage.direction === 'inbound' ? 'user' : 'assistant',
            content: newMessage.content ?? '',
            timestamp: new Date(newMessage.createdAt ?? Date.now()),
            isHidden: false,
            messageType: newMessage.messageType ?? 'text',
          };
          return [...prev, chatMessage];
        });

        // Refresh session data to get updated tool analytics
        getSessionQuery.refetch();
      },
      onError: (error) => {
        console.error('Error in newMessages subscription:', error);
      },
    }
  );

  // Polling fallback for new messages when WebSocket subscription doesn't work
  // (e.g., when worker runs in a separate process and activity events don't propagate)
  const lastMessageTimestamp = messages.length > 0
    ? messages[messages.length - 1].timestamp.toISOString()
    : undefined;

  const pollMessagesQuery = trpc.testSessions.pollMessages.useQuery(
    {
      sessionId: currentSession?.id ?? '',
      since: lastMessageTimestamp,
    },
    {
      enabled: !!currentSession?.id && !isLoading && !streamingState,
      refetchInterval: 5000, // Poll every 5 seconds
      refetchIntervalInBackground: false, // Don't poll when tab is not active
    }
  );

  // Handle polled messages
  useEffect(() => {
    if (pollMessagesQuery.data?.messages && pollMessagesQuery.data.messages.length > 0) {
      setMessages((prev) => {
        let updated = [...prev];
        let hasNewMessages = false;

        for (const msg of pollMessagesQuery.data.messages) {
          // Check if this message already exists
          const exists = updated.some((m) => m.id === msg.id);
          if (!exists) {
            hasNewMessages = true;
            const chatMessage: ChatMessage = {
              id: msg.id,
              role: msg.direction === 'inbound' ? 'user' : 'assistant',
              content: msg.content ?? '',
              timestamp: new Date(msg.createdAt ?? Date.now()),
              isHidden: false,
              messageType: msg.messageType ?? 'text',
            };
            updated = [...updated, chatMessage];
          }
        }

        // If new messages were added, refresh session data for tool analytics
        if (hasNewMessages) {
          getSessionQuery.refetch();
        }

        return updated;
      });
    }
  }, [pollMessagesQuery.data, getSessionQuery]);

  // Auto-scroll to bottom when new messages arrive or content streams
  // Skip auto-scroll when in simulation mode to allow user to review conversation
  useEffect(() => {
    if (!isSimulating) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamedContent, isSimulating]);

  // Initialize simulation settings from botDetails when loaded
  useEffect(() => {
    if (botDetails) {
      if (botDetails.testUserPrompt && !simulationPrompt) {
        setSimulationPrompt(botDetails.testUserPrompt);
      }
      if (botDetails.testUserContext && !simulationContext) {
        setSimulationContext(botDetails.testUserContext);
      }
    }
  }, [botDetails, simulationPrompt, simulationContext]);

  // Sync sessions from query
  useEffect(() => {
    if (sessionsQuery.data?.sessions) {
      setSessions(sessionsQuery.data.sessions);
    }
  }, [sessionsQuery.data]);

  // Load messages, tool analytics, and response format when session is selected
  useEffect(() => {
    if (getSessionQuery.data?.messages) {
      const loadedMessages: ChatMessage[] = getSessionQuery.data.messages.map((msg: {
        id: string;
        direction: string;
        messageType: string;
        content: string;
        isHidden: boolean | null;
        context?: unknown;
        status: string | null;
        platform: string | null;
        createdAt: string | null;
      }) => {
        const context = msg.context as Record<string, unknown> | undefined;
        return {
          id: msg.id,
          role: msg.direction === 'inbound' ? 'user' as const : 'assistant' as const,
          content: msg.content,
          timestamp: new Date(msg.createdAt ?? Date.now()),
          isHidden: msg.isHidden ?? false,
          messageType: msg.messageType,
          context,
          audioData: context?.audioData as string | undefined,
          audioContentType: context?.audioContentType as string | undefined,
        };
      });
      setMessages(loadedMessages);
    }
    // Load tool analytics
    if (getSessionQuery.data?.toolAnalytics) {
      setSessionToolAnalytics(getSessionQuery.data.toolAnalytics as SessionToolAnalytics);
    }
    // Load saved response format preference
    if (getSessionQuery.data?.responseFormat) {
      setNextResponseFormat(getSessionQuery.data.responseFormat as 'text' | 'voice');
    }
  }, [getSessionQuery.data]);

  // Delay auto-play of voice responses until UI is fully settled
  // This prevents the audio from being interrupted by React re-renders during state updates
  useEffect(() => {
    if (
      awaitingAutoPlayMessageId &&
      !isLoading &&
      !sessionStreamingState &&
      !getSessionQuery.isFetching
    ) {
      // Verify the message with audio data actually exists in the messages array
      // This ensures the refetch has completed and messages have been populated
      const messageWithAudio = messages.find(
        (m) => m.id === awaitingAutoPlayMessageId && m.audioData
      );

      if (messageWithAudio) {
        // Use a small timeout to ensure all React state updates have completed
        const timer = setTimeout(() => {
          setPendingAutoPlayMessageId(awaitingAutoPlayMessageId);
          setAwaitingAutoPlayMessageId(null);
        }, 100);
        return () => clearTimeout(timer);
      }
      // If message not found yet, the effect will re-run when messages change
    }
  }, [awaitingAutoPlayMessageId, isLoading, sessionStreamingState, getSessionQuery.isFetching, messages]);

  // Subscribe to streaming when streamingState is set
  trpc.bots.test.useSubscription(
    streamingState ? {
      botId: streamingState.botId,
      message: streamingState.message,
      history: streamingState.history,
    } : { botId: '', message: '', history: [] },
    {
      enabled: !!streamingState,
      onData: (chunk) => {
        if (!streamingState) return;

        if (chunk.type === 'start') {
          // Bot name received, store it
          if (chunk.botName) {
            setBotName(chunk.botName);
          }
        } else if (chunk.type === 'token' && chunk.content) {
          // Append token to streamed content
          setStreamedContent((prev) => prev + chunk.content);
        } else if (chunk.type === 'done') {
          // Stream complete, finalize the message
          const endTime = Date.now();
          const latency = endTime - streamingState.startTime;
          clearTypingIndicatorTimer(streamingState.messageId);

          setMessages((prev) => {
            const updated = [...prev];
            const msgIndex = updated.findIndex((m) => m.id === streamingState.messageId);
            if (msgIndex !== -1) {
              updated[msgIndex] = {
                ...updated[msgIndex],
                isStreaming: false,
                showTypingIndicator: false,
              };
            }
            return updated;
          });

          // Update debug info
          setDebugInfo({
            model: chunk.model || 'unknown',
            usage: chunk.usage || { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
            latency,
            rawResponse: {
              botName,
              model: chunk.model,
              usage: chunk.usage,
            },
          });

          // Reset streaming state
          setIsLoading(false);
          setStreamingState(null);
          setStreamedContent('');

          // If simulation is running, signal that it's the user's turn
          if (isSimulating) {
            setSimulationTurnState('waiting_for_delay');
          }
        } else if (chunk.type === 'error') {
          // Handle error
          clearTypingIndicatorTimer(streamingState.messageId);
          setMessages((prev) => {
            const updated = [...prev];
            const msgIndex = updated.findIndex((m) => m.id === streamingState.messageId);
            if (msgIndex !== -1) {
              updated[msgIndex] = {
                ...updated[msgIndex],
                content: `Error: ${chunk.error}`,
                isStreaming: false,
                showTypingIndicator: false,
              };
            }
            return updated;
          });

          setIsLoading(false);
          setStreamingState(null);
          setStreamedContent('');
        }
      },
      onError: (error) => {
        if (!streamingState) return;

        clearTypingIndicatorTimer(streamingState.messageId);
        setMessages((prev) => {
          const updated = [...prev];
          const msgIndex = updated.findIndex((m) => m.id === streamingState.messageId);
          if (msgIndex !== -1) {
            updated[msgIndex] = {
              ...updated[msgIndex],
              content: `Error: ${error.message}`,
              isStreaming: false,
              showTypingIndicator: false,
            };
          }
          return updated;
        });

        setIsLoading(false);
        setStreamingState(null);
        setStreamedContent('');
      },
    }
  );

  // Update the streaming message content as tokens arrive
  useEffect(() => {
    if (streamingState && streamedContent) {
      clearTypingIndicatorTimer(streamingState.messageId);
      setMessages((prev) => {
        const updated = [...prev];
        const msgIndex = updated.findIndex((m) => m.id === streamingState.messageId);
        if (msgIndex !== -1) {
          updated[msgIndex] = {
            ...updated[msgIndex],
            content: streamedContent,
          };
        }
        return updated;
      });
    }
  }, [streamedContent, streamingState, clearTypingIndicatorTimer]);

  // Subscribe to session streaming when sessionStreamingState is set
  trpc.testSessions.sendMessage.useSubscription(
    sessionStreamingState ? {
      sessionId: sessionStreamingState.sessionId,
      message: sessionStreamingState.message,
      attachments: sessionStreamingState.attachments,
      responseFormat: sessionStreamingState.responseFormat,
      voiceId: sessionStreamingState.voiceId,
    } : { sessionId: '', message: '' },
    {
      enabled: !!sessionStreamingState,
      onData: (chunk: {
        type: 'start' | 'token' | 'done' | 'error';
        botName?: string;
        content?: string;
        model?: string;
        usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
        error?: string;
        messageId?: string;
        latencyMs?: number;
        toolsExecuted?: ToolExecutionInfo[];
        toolContextMessageId?: string;
        responseFormat?: 'text' | 'voice';
        audioData?: string;
        audioContentType?: string;
      }) => {
        if (!sessionStreamingState) return;

        if (chunk.type === 'start') {
          if (chunk.botName) {
            setBotName(chunk.botName);
          }
        } else if (chunk.type === 'token' && chunk.content) {
          setStreamedContent((prev) => prev + chunk.content);
        } else if (chunk.type === 'done') {
          const endTime = Date.now();
          const latency = chunk.latencyMs ?? (endTime - sessionStreamingState.startTime);
          clearTypingIndicatorTimer(sessionStreamingState.messageId);

          setMessages((prev) => {
            const updated = [...prev];
            const msgIndex = updated.findIndex((m) => m.id === sessionStreamingState.messageId);
            if (msgIndex !== -1) {
              // For voice responses, set content here (since we skip streaming updates for voice)
              const isVoiceResponse = chunk.responseFormat === 'voice';
              updated[msgIndex] = {
                ...updated[msgIndex],
                content: isVoiceResponse ? streamedContent : updated[msgIndex].content,
                isStreaming: false,
                showTypingIndicator: false,
                // Include audio data if this was a voice response
                audioData: chunk.audioData,
                audioContentType: chunk.audioContentType,
                messageType: isVoiceResponse ? 'voice' : 'text',
              };
            }
            return updated;
          });

          setDebugInfo({
            model: chunk.model || 'unknown',
            usage: chunk.usage || { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
            latency,
            rawResponse: {
              botName,
              model: chunk.model,
              usage: chunk.usage,
              messageId: chunk.messageId,
              latencyMs: chunk.latencyMs,
              toolsExecuted: chunk.toolsExecuted,
              toolContextMessageId: chunk.toolContextMessageId,
              responseFormat: chunk.responseFormat,
            },
            toolsExecuted: chunk.toolsExecuted,
            toolContextMessageId: chunk.toolContextMessageId,
          });

          setIsLoading(false);
          setIsUploading(false);
          setSessionStreamingState(null);
          setStreamedContent('');

          // Schedule auto-play for new voice responses (delayed until UI settles)
          // Use chunk.messageId (backend-generated) instead of sessionStreamingState.messageId (client-generated)
          // because after refetch, messages will have the backend IDs
          if (chunk.responseFormat === 'voice' && chunk.audioData && chunk.messageId) {
            setAwaitingAutoPlayMessageId(chunk.messageId);
          }

          // Refresh session data to get any hidden messages (tool context) and updated tool analytics
          getSessionQuery.refetch();

          if (isSimulating) {
            setSimulationTurnState('waiting_for_delay');
          }
        } else if (chunk.type === 'error') {
          clearTypingIndicatorTimer(sessionStreamingState.messageId);
          setMessages((prev) => {
            const updated = [...prev];
            const msgIndex = updated.findIndex((m) => m.id === sessionStreamingState.messageId);
            if (msgIndex !== -1) {
              updated[msgIndex] = {
                ...updated[msgIndex],
                content: `Error: ${chunk.error}`,
                isStreaming: false,
                showTypingIndicator: false,
              };
            }
            return updated;
          });

          setIsLoading(false);
          setIsUploading(false);
          setSessionStreamingState(null);
          setStreamedContent('');
        }
      },
      onError: (error: Error) => {
        if (!sessionStreamingState) return;

        clearTypingIndicatorTimer(sessionStreamingState.messageId);
        setMessages((prev) => {
          const updated = [...prev];
          const msgIndex = updated.findIndex((m) => m.id === sessionStreamingState.messageId);
          if (msgIndex !== -1) {
            updated[msgIndex] = {
              ...updated[msgIndex],
              content: `Error: ${error.message}`,
              isStreaming: false,
              showTypingIndicator: false,
            };
          }
          return updated;
        });

        setIsLoading(false);
        setIsUploading(false);
        setSessionStreamingState(null);
        setStreamedContent('');
      },
    }
  );

  // Update the session streaming message content as tokens arrive
  // Skip this for voice responses - content will be set in the done handler to avoid flashing
  useEffect(() => {
    if (sessionStreamingState && streamedContent && sessionStreamingState.responseFormat !== 'voice') {
      clearTypingIndicatorTimer(sessionStreamingState.messageId);
      setMessages((prev) => {
        const updated = [...prev];
        const msgIndex = updated.findIndex((m) => m.id === sessionStreamingState.messageId);
        if (msgIndex !== -1) {
          updated[msgIndex] = {
            ...updated[msgIndex],
            content: streamedContent,
          };
        }
        return updated;
      });
    }
  }, [streamedContent, sessionStreamingState, clearTypingIndicatorTimer]);

  /**
   * Core send message function that can be called programmatically.
   * Uses session-based flow when a session is selected, otherwise uses legacy flow.
   * @param text - The message text to send
   * @param currentMessages - Current message history to build context from
   * @param attachmentsToSend - Optional attachments to include with the message
   */
  const sendMessage = useCallback((text: string, currentMessages: ChatMessage[], attachmentsToSend?: PendingAttachment[]) => {
    const hasContent = text.trim().length > 0;
    const hasAttachments = attachmentsToSend && attachmentsToSend.length > 0;

    if ((!hasContent && !hasAttachments) || isLoading || !botId || isTypingDelaySettingsPending) return;

    // File attachments require a session to be selected (legacy flow doesn't support attachments)
    if (hasAttachments && !currentSession) {
      alert('File attachments require a session. Please create or select a session first using the "+ New Session" button.');
      return;
    }

    // Convert pending attachments to MessageAttachment format for the user message
    const messageAttachments: MessageAttachment[] | undefined = hasAttachments
      ? attachmentsToSend.map((a) => ({
          type: a.file.type.startsWith('image/') ? 'image' as const : 'document' as const,
          data: a.base64Data,
          mimeType: a.file.type,
          filename: a.file.name,
        }))
      : undefined;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
      attachments: messageAttachments,
    };

    // Create placeholder for bot response
    // When requesting voice response, mark it as voice from the start to prevent flashing
    const botMessageId = crypto.randomUUID();
    const isVoiceRequest = currentSession && nextResponseFormat === 'voice';
    const botMessage = createPendingAssistantMessage(
      botMessageId,
      new Date(),
      isVoiceRequest ? 'voice' : 'text',
      typingDelayMs
    );

    // Add user message and bot placeholder
    setMessages((prev) => [...prev, userMessage, botMessage]);
    if (typingDelayMs > 0) {
      const timer = setTimeout(() => {
        typingIndicatorTimersRef.current.delete(botMessageId);
        setMessages((prev) => prev.map((message) => (
          message.id === botMessageId && message.isStreaming && !message.content
            ? { ...message, showTypingIndicator: true }
            : message
        )));
      }, typingDelayMs);
      typingIndicatorTimersRef.current.set(botMessageId, timer);
    }
    setIsLoading(true);
    setIsUploading(true);
    setStreamedContent('');

    // If simulation is active, mark that we're waiting for bot
    if (isSimulating) {
      setSimulationTurnState('waiting_for_bot');
    }

    // Convert attachments to API format (strip data URI prefix)
    const apiAttachments: AttachmentInput[] | undefined = hasAttachments
      ? attachmentsToSend.map((a) => ({
          type: a.file.type.startsWith('image/') ? 'image' as const : 'document' as const,
          // Strip data URI prefix (e.g., "data:image/jpeg;base64,")
          data: a.base64Data.replace(/^data:[^;]+;base64,/, ''),
          mimeType: a.file.type,
          filename: a.file.name,
        }))
      : undefined;

    // Use session-based flow if a session is selected
    if (currentSession) {
      setSessionStreamingState({
        sessionId: currentSession.id,
        message: userMessage.content,
        startTime: Date.now(),
        messageId: botMessageId,
        attachments: apiAttachments,
        responseFormat: nextResponseFormat,
        voiceId: nextResponseFormat === 'voice' ? selectedVoiceId : undefined,
      });
    } else {
      // Legacy flow (in-memory only, no persistence)
      // Build history from current messages (excluding the new messages)
      const history = currentMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      // Start streaming subscription
      setStreamingState({
        botId,
        message: userMessage.content,
        history,
        startTime: Date.now(),
        messageId: botMessageId,
      });
    }
  }, [isLoading, botId, isSimulating, currentSession, nextResponseFormat, selectedVoiceId, typingDelayMs, isTypingDelaySettingsPending]);

  /**
   * Handler for user-initiated message sends (from input field)
   */
  const handleSendMessage = useCallback(() => {
    const hasContent = inputMessage.trim().length > 0;
    const hasAttachments = pendingAttachments.length > 0;

    if (!hasContent && !hasAttachments) return;

    sendMessage(inputMessage.trim(), messages, pendingAttachments.length > 0 ? pendingAttachments : undefined);
    setInputMessage('');
    setPendingAttachments([]);
  }, [inputMessage, messages, pendingAttachments, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    typingIndicatorTimersRef.current.forEach((timer) => clearTimeout(timer));
    typingIndicatorTimersRef.current.clear();
    setMessages([]);
    setDebugInfo(null);
    setInputMessage('');
    setPendingAttachments([]);
    setStreamingState(null);
    setStreamedContent('');
    setIsLoading(false);
    setIsUploading(false);
    // Also stop simulation if running
    if (isSimulating) {
      setIsSimulating(false);
      setSimulationTurnState('idle');
      if (simulationDelayTimerRef.current) {
        clearTimeout(simulationDelayTimerRef.current);
        simulationDelayTimerRef.current = null;
      }
    }
  };

  /**
   * Generates and sends a simulated user message
   */
  const handleSimulateNextMessage = useCallback(async () => {
    if (!botId || !simulationPrompt.trim() || isLoading || isTypingDelaySettingsPending) return;

    setSimulationTurnState('generating_user');

    try {
      // Build history from current messages for the simulated user
      const history = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const result = await generateSimulatedUserMutation.mutateAsync({
        botId,
        systemPrompt: simulationPrompt,
        context: simulationContext || undefined,
        history,
      });

      if (result.response && isSimulating) {
        // Send the simulated user's message to the bot
        sendMessage(result.response, messages);
      }
    } catch (error) {
      console.error('Failed to generate simulated user response:', error);
      // Stop simulation on error
      setIsSimulating(false);
      setSimulationTurnState('idle');
    }
  }, [botId, simulationPrompt, simulationContext, messages, isLoading, isSimulating, generateSimulatedUserMutation, sendMessage, isTypingDelaySettingsPending]);

  /**
   * Toggles the simulation on/off
   */
  const handleToggleSimulation = useCallback(async () => {
    if (isSimulating) {
      // Stop simulation
      setIsSimulating(false);
      setSimulationTurnState('idle');
      if (simulationDelayTimerRef.current) {
        clearTimeout(simulationDelayTimerRef.current);
        simulationDelayTimerRef.current = null;
      }
    } else {
      // Start simulation
      if (isTypingDelaySettingsPending) {
        return;
      }

      if (!simulationPrompt.trim()) {
        alert('Please enter a simulation prompt before starting.');
        return;
      }

      if (!botId) return;

      // Save simulation settings to database
      try {
        await updateBotMutation.mutateAsync({
          id: botId,
          testUserPrompt: simulationPrompt,
          testUserContext: simulationContext || null,
        });
      } catch (error) {
        console.error('Failed to save simulation settings:', error);
        // Continue anyway - don't block simulation
      }

      setIsSimulating(true);

      // If there are no messages yet, or the last message is from the user,
      // wait for the bot to respond first
      const lastMessage = messages[messages.length - 1];
      if (messages.length === 0) {
        // No messages - generate the first simulated user message immediately (no delay)
        setSimulationTurnState('generating_user');
        // Call generateSimulatedUserResponse directly since the useCallback may not have the latest isSimulating value yet
        generateSimulatedUserMutation.mutateAsync({
          botId,
          systemPrompt: simulationPrompt,
          context: simulationContext || undefined,
          history: [],
        }).then((result) => {
          if (result.response) {
            sendMessage(result.response, []);
          }
        }).catch((error) => {
          console.error('Failed to generate simulated user response:', error);
          setIsSimulating(false);
          setSimulationTurnState('idle');
        });
      } else if (lastMessage?.role === 'assistant' && !lastMessage.isStreaming) {
        // Bot just responded - it's user's turn
        setSimulationTurnState('waiting_for_delay');
      } else if (lastMessage?.isStreaming || isLoading) {
        // Bot is currently responding
        setSimulationTurnState('waiting_for_bot');
      } else {
        // User's turn (last message was from user or other state)
        setSimulationTurnState('waiting_for_delay');
      }
    }
  }, [isSimulating, simulationPrompt, simulationContext, botId, messages, isLoading, updateBotMutation, generateSimulatedUserMutation, sendMessage, isTypingDelaySettingsPending]);

  /**
   * Handle file selection for attachments
   */
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: PendingAttachment[] = [];

    for (const file of Array.from(files)) {
      // Validate file type
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        alert(`File type not supported: ${file.name}`);
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large (max 10MB): ${file.name}`);
        continue;
      }

      // Read file as base64
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

      // Generate preview for images
      const preview = file.type.startsWith('image/') ? base64Data : null;

      newAttachments.push({ file, preview, base64Data });
    }

    setPendingAttachments((prev) => [...prev, ...newAttachments].slice(0, 5)); // Max 5 attachments

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  /**
   * Remove a pending attachment
   */
  const handleRemoveAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Auto-reply loop: when it's the user's turn and simulation is active
  useEffect(() => {
    if (!isSimulating || simulationTurnState !== 'waiting_for_delay') {
      return;
    }

    // Clear any existing timer
    if (simulationDelayTimerRef.current) {
      clearTimeout(simulationDelayTimerRef.current);
    }

    // Wait for the configured delay, then generate simulated user message
    simulationDelayTimerRef.current = setTimeout(() => {
      handleSimulateNextMessage();
    }, simulationDelay);

    return () => {
      if (simulationDelayTimerRef.current) {
        clearTimeout(simulationDelayTimerRef.current);
        simulationDelayTimerRef.current = null;
      }
    };
  }, [isSimulating, simulationTurnState, simulationDelay, handleSimulateNextMessage]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (simulationDelayTimerRef.current) {
        clearTimeout(simulationDelayTimerRef.current);
      }
      typingIndicatorTimersRef.current.forEach((timer) => clearTimeout(timer));
      typingIndicatorTimersRef.current.clear();
    };
  }, []);

  if (contextLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (!botId) {
    return (
      <div style={styles.error}>
        No bot selected. Please select a bot from the sidebar.
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/bot/details')} style={styles.backButton}>
          &larr; Back to Bot Details
        </button>
        <h1 style={styles.title}>Test Bot</h1>
        {currentBot && (
          <p style={styles.subtitle}>
            Testing: {currentBot.icon || '🤖'} {currentBot.name}
          </p>
        )}
      </div>

      {/* Session Management Bar */}
      <div style={styles.sessionBar}>
        <div style={styles.sessionBarLeft}>
          <span style={styles.sessionLabel}>Session:</span>
          {currentSession ? (
            <div style={styles.currentSessionInfo}>
              <span style={styles.sessionName}>{currentSession.name}</span>
              <span style={styles.sessionBadge}>
                Messages are saved
              </span>
            </div>
          ) : (
            <span style={styles.noSessionText}>No session (messages not persisted)</span>
          )}
        </div>
        <div style={styles.sessionBarRight}>
          {currentSession && (
            <button
              onClick={() => {
                setCurrentSession(null);
                setMessages([]);
              }}
              style={styles.sessionButton}
            >
              Leave Session
            </button>
          )}
          <button
            onClick={() => setShowSessionList(!showSessionList)}
            style={styles.sessionButton}
          >
            {showSessionList ? 'Hide Sessions' : 'Manage Sessions'}
          </button>
          {!isCreatingSession ? (
            <button
              onClick={() => setIsCreatingSession(true)}
              style={styles.sessionButtonPrimary}
            >
              + New Session
            </button>
          ) : (
            <div style={styles.createSessionForm}>
              <input
                type="text"
                placeholder="Session name..."
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                style={styles.sessionNameInput}
                autoFocus
              />
              <button
                onClick={() => {
                  if (botId) {
                    createSessionMutation.mutate({
                      botId,
                      name: newSessionName || undefined,
                    });
                  }
                }}
                disabled={createSessionMutation.isPending}
                style={styles.sessionButtonPrimary}
              >
                {createSessionMutation.isPending ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => {
                  setIsCreatingSession(false);
                  setNewSessionName('');
                }}
                style={styles.sessionButton}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Session List Dropdown */}
      {showSessionList && (
        <div style={styles.sessionList}>
          <div style={styles.sessionListHeader}>
            <span style={styles.sessionListTitle}>Previous Sessions</span>
            <label style={styles.showHiddenLabel}>
              <input
                type="checkbox"
                checked={showHiddenMessages}
                onChange={(e) => setShowHiddenMessages(e.target.checked)}
              />
              Show hidden messages
            </label>
          </div>
          {sessions.length === 0 ? (
            <div style={styles.noSessions}>No sessions yet. Create one to get started.</div>
          ) : (
            <div style={styles.sessionItems}>
              {sessions.map((session) => (
                <div
                  key={session.id}
                  style={{
                    ...styles.sessionItem,
                    ...(currentSession?.id === session.id ? styles.sessionItemActive : {}),
                  }}
                  onClick={() => {
                    setCurrentSession(session);
                    setShowSessionList(false);
                  }}
                >
                  <div style={styles.sessionItemInfo}>
                    <span style={styles.sessionItemName}>{session.name}</span>
                    <span style={styles.sessionItemDate}>
                      {session.lastActivityAt
                        ? new Date(session.lastActivityAt).toLocaleString()
                        : session.createdAt
                          ? new Date(session.createdAt).toLocaleString()
                          : 'Unknown'}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('Archive this session?')) {
                        archiveSessionMutation.mutate({ sessionId: session.id });
                      }
                    }}
                    style={styles.archiveButton}
                  >
                    Archive
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={styles.mainLayout}>
        {/* Chat Area */}
        <div style={styles.chatArea}>
          <div style={styles.chatCard}>
            <div style={styles.chatHeader}>
              <div style={styles.chatHeaderLeft}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M5 1L5 3M11 1V3M5 13V15M11 13V15M1 5H3M1 11H3M13 5H15M13 11H15" stroke="#5A6A8A" strokeWidth="1.5" strokeLinecap="round"/>
                  <rect x="4.5" y="4.5" width="7" height="7" rx="1" stroke="#5A6A8A" strokeWidth="1.5"/>
                </svg>
                <span style={styles.chatTitle}>Conversation</span>
              </div>
              <div style={styles.streamingIndicator}>
                {isLoading && (
                  <span style={styles.streamingBadge}>Streaming...</span>
                )}
              </div>
            </div>

            {/* Messages Container */}
            <div style={styles.messagesContainer}>
              {messages.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={styles.emptyIcon}>💬</div>
                  <p style={styles.emptyText}>Start a conversation with your bot</p>
                  <p style={styles.emptySubtext}>
                    {currentSession
                      ? 'Messages are being persisted to the database. Hidden messages and tool analytics will be visible.'
                      : 'Create a session to persist messages and see hidden messages/tool analytics.'}
                  </p>
                </div>
              ) : (
                <>
                  {messages
                    .filter((message) => showHiddenMessages || !message.isHidden)
                    .map((message) => (
                    <div
                      key={message.id}
                      style={{
                        ...styles.messageWrapper,
                        justifyContent: message.isHidden
                          ? 'center'
                          : message.role === 'user'
                            ? 'flex-end'
                            : 'flex-start',
                      }}
                    >
                      <div
                        onClick={() => setSelectedMessageId(
                          selectedMessageId === message.id ? null : message.id
                        )}
                        style={{
                          ...styles.messageBubble,
                          ...(message.isHidden
                            ? styles.hiddenBubble
                            : message.role === 'user'
                              ? styles.userBubble
                              : styles.botBubble),
                          ...(selectedMessageId === message.id ? styles.selectedBubble : {}),
                          cursor: 'pointer',
                        }}
                      >
                        {message.isHidden && (
                          <div style={styles.hiddenBadge}>
                            {message.messageType === 'tool_context' ? 'Tool Context' : 'System Context'}
                          </div>
                        )}
                        <div style={styles.messageContent}>
                          {/* Voice badge when audio data is available */}
                          {message.audioData && (
                            <div style={styles.voiceBadge}>
                              🎙️ Voice Response
                            </div>
                          )}
                          {message.isHidden && message.messageType === 'tool_context' ? (
                            <pre style={styles.toolContextContent}>
                              {((): string => {
                                try {
                                  const parsed = JSON.parse(message.content);
                                  return JSON.stringify(parsed, null, 2);
                                } catch {
                                  return message.content;
                                }
                              })()}
                            </pre>
                          ) : (
                            message.content || (message.isStreaming && message.showTypingIndicator && (
                              <div style={styles.typingIndicator}>
                                <span style={styles.typingDot} />
                                <span style={{ ...styles.typingDot, animationDelay: '0.2s' }} />
                                <span style={{ ...styles.typingDot, animationDelay: '0.4s' }} />
                              </div>
                            ))
                          )}
                          {message.isStreaming && message.content && (
                            <span style={styles.cursor}>▊</span>
                          )}
                          {/* Render audio player when audio data is available */}
                          {message.audioData && (
                            <div style={styles.audioPlayerContainer}>
                              <audio
                                // Use stable key with autoplay suffix only for messages that need auto-play
                                // The key includes autoplay suffix once set (either pending or already playing)
                                // This prevents remounting during playback which would stop the audio
                                key={
                                  message.id === pendingAutoPlayMessageId || autoPlayingMessageIds.has(message.id)
                                    ? `${message.id}-autoplay`
                                    : message.id
                                }
                                controls
                                autoPlay={message.id === pendingAutoPlayMessageId}
                                ref={(audioEl) => {
                                  // Programmatically play if autoPlay attribute fails (browser policy)
                                  // Check both conditions and that audio is paused to avoid duplicate play calls
                                  if (audioEl && message.id === pendingAutoPlayMessageId && audioEl.paused) {
                                    audioEl.play().catch(() => {
                                      // Autoplay was blocked by browser policy, user will need to click play
                                    });
                                  }
                                }}
                                onPlay={() => {
                                  // Track that this message has started auto-playing to keep the key stable
                                  if (message.id === pendingAutoPlayMessageId) {
                                    setAutoPlayingMessageIds((prev) => new Set(prev).add(message.id));
                                    setPendingAutoPlayMessageId(null);
                                  }
                                }}
                                onCanPlayThrough={(e) => {
                                  // Additional fallback: try to play when audio is fully loaded
                                  const audioEl = e.currentTarget;
                                  if (message.id === pendingAutoPlayMessageId && audioEl.paused) {
                                    audioEl.play().catch(() => {
                                      // Autoplay was blocked by browser policy
                                    });
                                  }
                                }}
                                style={styles.audioPlayer}
                                src={`data:${message.audioContentType || 'audio/mpeg'};base64,${message.audioData}`}
                              />
                            </div>
                          )}
                          {/* Render attachments if present */}
                          {message.attachments && message.attachments.length > 0 && (
                            <div style={styles.messageAttachmentsContainer}>
                              {message.attachments.map((attachment, idx) => (
                                <div key={idx} style={styles.messageAttachmentItem}>
                                  {attachment.type === 'image' ? (
                                    attachment.url ? (
                                      <a
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={styles.attachmentImageLink}
                                      >
                                        <img
                                          src={attachment.url}
                                          alt={attachment.filename || 'Image'}
                                          style={styles.messageAttachmentImage}
                                        />
                                      </a>
                                    ) : attachment.data ? (
                                      <img
                                        src={attachment.data}
                                        alt={attachment.filename || 'Image'}
                                        style={styles.messageAttachmentImage}
                                      />
                                    ) : null
                                  ) : (
                                    <div
                                      style={{
                                        ...styles.messageAttachmentFile,
                                        ...(message.role === 'user'
                                          ? styles.userAttachmentFile
                                          : {}),
                                      }}
                                    >
                                      <span style={styles.attachmentFileIcon}>
                                        {attachment.mimeType?.includes('pdf') ? '📄' :
                                         attachment.mimeType?.includes('audio') ? '🎵' :
                                         attachment.mimeType?.includes('video') ? '🎬' : '📎'}
                                      </span>
                                      <span style={styles.attachmentFileName}>
                                        {attachment.filename || 'Document'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={styles.messageTime}>
                          {message.timestamp.toLocaleTimeString()}
                          {/* Show latency for bot responses */}
                          {message.role === 'assistant' && !message.isHidden && typeof message.context?.latencyMs === 'number' && (
                            <span style={styles.latencyBadge}>
                              {message.context.latencyMs}ms
                            </span>
                          )}
                          {/* Show tools executed */}
                          {Array.isArray(message.context?.toolsExecuted) && message.context.toolsExecuted.length > 0 && (
                            <span style={styles.toolsBadge}>
                              Tools: {(message.context.toolsExecuted as string[]).join(', ')}
                            </span>
                          )}
                          {/* Show message type for introspection */}
                          {message.messageType && message.messageType !== 'text' && (
                            <span style={styles.messageTypeBadge}>
                              {message.messageType}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            <div style={styles.inputAreaWrapper}>
              {/* Pending Attachments Preview */}
              {pendingAttachments.length > 0 && (
                <div style={styles.attachmentsPreviewContainer}>
                  {pendingAttachments.map((attachment, index) => (
                    <div key={index} style={styles.attachmentPreviewItem}>
                      {attachment.preview ? (
                        <img
                          src={attachment.preview}
                          alt={attachment.file.name}
                          style={styles.attachmentPreviewImage}
                        />
                      ) : (
                        <div style={styles.attachmentPreviewDoc}>
                          <span style={styles.attachmentPreviewDocIcon}>
                            {attachment.file.type.includes('pdf') ? '📄' :
                             attachment.file.type.includes('audio') ? '🎵' :
                             attachment.file.type.includes('video') ? '🎬' : '📎'}
                          </span>
                        </div>
                      )}
                      <span style={styles.attachmentPreviewName}>
                        {attachment.file.name.length > 15
                          ? attachment.file.name.substring(0, 12) + '...'
                          : attachment.file.name}
                      </span>
                      <button
                        onClick={() => handleRemoveAttachment(index)}
                        style={styles.attachmentRemoveButton}
                        title="Remove attachment"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={styles.inputArea}>
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  multiple
                  accept={ALLOWED_MIME_TYPES.join(',')}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    ...styles.attachButton,
                    ...(!currentSession && { opacity: 0.5, cursor: 'not-allowed' }),
                  }}
                  disabled={isUploading || pendingAttachments.length >= 5 || !currentSession}
                  title={currentSession ? 'Attach files (max 5)' : 'Create a session to attach files'}
                >
                  📎
                </button>
                <textarea
                  placeholder={isTypingDelaySettingsPending ? 'Loading WhatsApp typing delay...' : 'Type your message...'}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={styles.textInput}
                  rows={2}
                  disabled={isLoading || isTypingDelaySettingsPending}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isLoading || isTypingDelaySettingsPending || (!inputMessage.trim() && pendingAttachments.length === 0)}
                  style={{
                    ...styles.sendButton,
                    ...(isLoading || isTypingDelaySettingsPending || (!inputMessage.trim() && pendingAttachments.length === 0) ? styles.sendButtonDisabled : {}),
                  }}
                >
                  {isLoading ? (
                    <div style={styles.spinner} />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M18.5 1.5L9 11M18.5 1.5L12.5 18.5L9 11M18.5 1.5L1.5 7.5L9 11"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Debug Side Panel */}
        <div style={styles.debugPanel}>
          <div style={styles.debugCard}>
            <div style={styles.debugHeader}>
              <span style={styles.debugTitle}>Debug Info</span>
              {messages.length > 0 && (
                <button onClick={handleClearChat} style={styles.clearButton}>
                  Clear Chat
                </button>
              )}
            </div>

            {/* System Prompt Section */}
            <div style={styles.debugSection}>
              <div style={styles.debugSectionTitle}>System Prompt</div>
              <div style={styles.systemPromptContainer}>
                <pre style={styles.systemPrompt}>
                  {botDetails?.systemPrompt || 'No system prompt configured'}
                </pre>
              </div>
            </div>

            {/* Response Format Toggle */}
            <div style={styles.debugSection}>
              <div style={styles.debugSectionTitle}>Response Format</div>
              <div style={styles.responseFormatToggleContainer}>
                <button
                  onClick={() => {
                    setNextResponseFormat('text');
                    // Save preference if a session is selected
                    if (currentSession?.id) {
                      updateResponseFormatMutation.mutate({
                        sessionId: currentSession.id,
                        responseFormat: 'text',
                      });
                    }
                  }}
                  style={{
                    ...styles.responseFormatButton,
                    ...(nextResponseFormat === 'text' ? styles.responseFormatButtonActive : {}),
                  }}
                  disabled={isLoading}
                >
                  Text
                </button>
                <button
                  onClick={() => {
                    setNextResponseFormat('voice');
                    // Save preference if a session is selected
                    if (currentSession?.id) {
                      updateResponseFormatMutation.mutate({
                        sessionId: currentSession.id,
                        responseFormat: 'voice',
                      });
                    }
                  }}
                  style={{
                    ...styles.responseFormatButton,
                    ...(nextResponseFormat === 'voice' ? styles.responseFormatButtonActive : {}),
                  }}
                  disabled={isLoading}
                >
                  Voice
                </button>
              </div>
              <p style={styles.responseFormatHint}>
                {nextResponseFormat === 'voice'
                  ? 'Next response will be generated as audio using TTS'
                  : 'Next response will be text only'}
              </p>

              {/* Voice selector - only shown when voice mode is selected */}
              {nextResponseFormat === 'voice' && (
                <div style={styles.voiceSelectorContainer}>
                  <label style={styles.voiceSelectorLabel}>Voice</label>
                  <select
                    value={selectedVoiceId}
                    onChange={(e) => setSelectedVoiceId(e.target.value as TTSVoiceId)}
                    style={styles.voiceSelectorSelect}
                    disabled={isLoading}
                  >
                    {TTS_VOICES.map((voice) => (
                      <option key={voice.value} value={voice.value}>
                        {voice.label} - {voice.description}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* User Simulation Section */}
            <div style={styles.debugSection}>
              <div style={styles.debugSectionTitle}>
                User Simulation
                {isSimulating && (
                  <span style={styles.simulationBadge}>
                    {simulationTurnState === 'waiting_for_bot' && 'Waiting for bot...'}
                    {simulationTurnState === 'waiting_for_delay' && 'Waiting...'}
                    {simulationTurnState === 'generating_user' && 'Generating...'}
                  </span>
                )}
              </div>

              {/* Simulation Prompt */}
              <div style={styles.simulationField}>
                <label style={styles.simulationLabel}>User Persona Prompt</label>
                <textarea
                  value={simulationPrompt}
                  onChange={(e) => setSimulationPrompt(e.target.value)}
                  placeholder="Describe the simulated user's persona, personality, and goals..."
                  style={styles.simulationTextarea}
                  rows={3}
                  disabled={isSimulating}
                />
              </div>

              {/* Simulation Context */}
              <div style={styles.simulationField}>
                <label style={styles.simulationLabel}>Background Context (optional)</label>
                <textarea
                  value={simulationContext}
                  onChange={(e) => setSimulationContext(e.target.value)}
                  placeholder="Previous conversations, user history, or other context..."
                  style={styles.simulationTextarea}
                  rows={2}
                  disabled={isSimulating}
                />
              </div>

              {/* Delay Input */}
              <div style={styles.simulationField}>
                <label style={styles.simulationLabel}>Response Delay (seconds)</label>
                <input
                  type="number"
                  value={simulationDelay / 1000}
                  onChange={(e) => setSimulationDelay(Math.max(0.5, parseFloat(e.target.value) || 1) * 1000)}
                  min="0.5"
                  max="30"
                  step="0.5"
                  style={styles.simulationInput}
                  disabled={isSimulating}
                />
              </div>

              {/* Start/Stop Button */}
              <button
                onClick={handleToggleSimulation}
                disabled={isTypingDelaySettingsPending || updateBotMutation.isPending || generateSimulatedUserMutation.isPending}
                style={{
                  ...styles.simulationButton,
                  ...(isSimulating ? styles.simulationButtonStop : styles.simulationButtonStart),
                  ...((isTypingDelaySettingsPending || updateBotMutation.isPending || generateSimulatedUserMutation.isPending) ? styles.simulationButtonDisabled : {}),
                }}
              >
                {isTypingDelaySettingsPending ? 'Loading WhatsApp settings...' :
                  updateBotMutation.isPending ? 'Saving...' :
                  generateSimulatedUserMutation.isPending ? 'Generating...' :
                    isSimulating ? 'Stop Simulation' : 'Start Simulation'}
              </button>
            </div>

            {/* Last Response Stats */}
            {debugInfo && (
              <div style={styles.debugSection}>
                <div style={styles.debugSectionTitle}>Last Response Stats</div>
                <div style={styles.statsGrid}>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Model</span>
                    <span style={styles.statValue}>{debugInfo.model}</span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Latency</span>
                    <span style={styles.statValue}>{debugInfo.latency}ms</span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Prompt Tokens</span>
                    <span style={styles.statValue}>{debugInfo.usage.promptTokens}</span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Completion Tokens</span>
                    <span style={styles.statValue}>{debugInfo.usage.completionTokens}</span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Total Tokens</span>
                    <span style={styles.statValue}>{debugInfo.usage.totalTokens}</span>
                  </div>
                </div>
                {/* Tools executed in this response */}
                {debugInfo.toolsExecuted && debugInfo.toolsExecuted.length > 0 && (
                  <div style={styles.toolExecutionList}>
                    <div style={styles.toolExecutionHeader}>Tools Executed:</div>
                    {debugInfo.toolsExecuted.map((tool, idx) => (
                      <div key={idx} style={styles.toolExecutionItem}>
                        <span style={styles.toolExecutionName}>{tool.toolName}</span>
                        <span style={{
                          ...styles.toolExecutionStatus,
                          ...(tool.success ? styles.toolExecutionSuccess : styles.toolExecutionFailure),
                        }}>
                          {tool.success ? '✓' : '✗'} {tool.durationMs}ms
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Session Tool Analytics */}
            {currentSession && sessionToolAnalytics && sessionToolAnalytics.totalExecutions > 0 && (
              <div style={styles.debugSection}>
                <div style={styles.debugSectionTitle}>Session Tool Analytics</div>
                <div style={styles.statsGrid}>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Total Executions</span>
                    <span style={styles.statValue}>{sessionToolAnalytics.totalExecutions}</span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Success Rate</span>
                    <span style={styles.statValue}>
                      {sessionToolAnalytics.totalExecutions > 0
                        ? Math.round((sessionToolAnalytics.successfulExecutions / sessionToolAnalytics.totalExecutions) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Avg Latency</span>
                    <span style={styles.statValue}>
                      {sessionToolAnalytics.avgLatencyMs ?? '-'}ms
                    </span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statLabel}>Failures</span>
                    <span style={{
                      ...styles.statValue,
                      color: sessionToolAnalytics.failedExecutions > 0 ? '#ef4444' : '#22c55e',
                    }}>
                      {sessionToolAnalytics.failedExecutions}
                    </span>
                  </div>
                </div>
                {/* Tool breakdown */}
                {sessionToolAnalytics.toolBreakdown.length > 0 && (
                  <div style={styles.toolBreakdownContainer}>
                    <div style={styles.toolBreakdownTitle}>By Tool:</div>
                    {sessionToolAnalytics.toolBreakdown.map((tool) => (
                      <div key={tool.toolId} style={styles.toolBreakdownRow}>
                        <span style={styles.toolBreakdownName}>{tool.toolName}</span>
                        <span style={styles.toolBreakdownStats}>
                          {tool.executions}x | {tool.successRate}% | {tool.avgLatencyMs ?? '-'}ms
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Message Introspection Panel */}
            {selectedMessageId && (
              <div style={styles.debugSection}>
                <div style={styles.debugSectionTitle}>
                  Message Introspection
                  <button
                    onClick={() => setSelectedMessageId(null)}
                    style={styles.closeIntrospectionButton}
                  >
                    ×
                  </button>
                </div>
                {(() => {
                  const msg = messages.find(m => m.id === selectedMessageId);
                  if (!msg) return <div style={styles.noDebugText}>Message not found</div>;
                  return (
                    <div style={styles.introspectionContent}>
                      <div style={styles.introspectionRow}>
                        <span style={styles.introspectionLabel}>ID:</span>
                        <span style={styles.introspectionValue}>{msg.id}</span>
                      </div>
                      <div style={styles.introspectionRow}>
                        <span style={styles.introspectionLabel}>Role:</span>
                        <span style={styles.introspectionValue}>{msg.role}</span>
                      </div>
                      <div style={styles.introspectionRow}>
                        <span style={styles.introspectionLabel}>Type:</span>
                        <span style={styles.introspectionValue}>{msg.messageType ?? 'text'}</span>
                      </div>
                      <div style={styles.introspectionRow}>
                        <span style={styles.introspectionLabel}>Hidden:</span>
                        <span style={styles.introspectionValue}>{msg.isHidden ? 'Yes' : 'No'}</span>
                      </div>
                      <div style={styles.introspectionRow}>
                        <span style={styles.introspectionLabel}>Time:</span>
                        <span style={styles.introspectionValue}>{msg.timestamp.toISOString()}</span>
                      </div>
                      {msg.context && Object.keys(msg.context).length > 0 && (
                        <>
                          <div style={styles.introspectionContextTitle}>Context:</div>
                          <div style={styles.jsonContainer}>
                            <pre style={styles.jsonOutput}>
                              {JSON.stringify(msg.context, null, 2)}
                            </pre>
                          </div>
                        </>
                      )}
                      <div style={styles.introspectionContextTitle}>Content:</div>
                      <div style={styles.introspectionContentBox}>
                        <pre style={styles.introspectionContentPre}>{msg.content}</pre>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Raw JSON Response */}
            {debugInfo && (
              <div style={styles.debugSection}>
                <div style={styles.debugSectionTitle}>Raw Response</div>
                <div style={styles.jsonContainer}>
                  <pre style={styles.jsonOutput}>
                    {JSON.stringify(debugInfo.rawResponse, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {!debugInfo && !selectedMessageId && (
              <div style={styles.noDebugInfo}>
                <p style={styles.noDebugText}>
                  Send a message to see response stats and debug information.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    marginBottom: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  mainLayout: {
    display: 'flex',
    gap: '24px',
    flex: 1,
    minHeight: 0,
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  chatCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  chatHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  chatTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
  },
  streamingIndicator: {
    display: 'flex',
    alignItems: 'center',
  },
  streamingBadge: {
    fontSize: '12px',
    color: '#22c55e',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '40px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#1e293b',
    margin: '0 0 8px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
    maxWidth: '300px',
  },
  messageWrapper: {
    display: 'flex',
    width: '100%',
  },
  messageBubble: {
    maxWidth: '75%',
    padding: '12px 16px',
    borderRadius: '16px',
    wordWrap: 'break-word',
  },
  userBubble: {
    backgroundColor: '#5A6A8A',
    color: '#FAF9F7',
    borderBottomRightRadius: '4px',
  },
  botBubble: {
    backgroundColor: '#f1f5f9',
    color: '#1e293b',
    borderBottomLeftRadius: '4px',
  },
  hiddenBubble: {
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
    borderRadius: '8px',
    border: '1px dashed #9ca3af',
    maxWidth: '90%',
  },
  hiddenBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    backgroundColor: '#9ca3af',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 600,
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  messageContent: {
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  messageTime: {
    fontSize: '11px',
    opacity: 0.7,
    marginTop: '6px',
  },
  typingIndicator: {
    display: 'flex',
    gap: '4px',
    padding: '4px 0',
  },
  typingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#94a3b8',
    animation: 'typing 1s infinite ease-in-out',
  },
  cursor: {
    animation: 'blink 1s step-end infinite',
    marginLeft: '2px',
  },
  inputArea: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    flexShrink: 0,
  },
  textInput: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'none',
    outline: 'none',
    boxSizing: 'border-box',
    lineHeight: '1.5',
  },
  sendButton: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #5A6A8A 0%, #4A5A7A 100%)',
    border: 'none',
    color: '#FAF9F7',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sendButtonDisabled: {
    background: 'rgba(93, 86, 78, 0.1)',
    color: '#9B9285',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid rgba(155, 146, 133, 0.3)',
    borderTopColor: '#9B9285',
    animation: 'spin 1s linear infinite',
  },
  debugPanel: {
    width: '380px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  debugCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  debugHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  debugTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
  },
  clearButton: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  debugSection: {
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
  },
  debugSectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
  },
  systemPromptContainer: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: '150px',
    overflowY: 'auto',
  },
  systemPrompt: {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#475569',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
    fontFamily: 'monospace',
  },
  jsonContainer: {
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  jsonOutput: {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#94a3b8',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
  },
  noDebugInfo: {
    padding: '40px 20px',
    textAlign: 'center',
  },
  noDebugText: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: 0,
  },
  // Response format toggle styles
  responseFormatToggleContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
  },
  responseFormatButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 500,
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  responseFormatButtonActive: {
    backgroundColor: '#5A6A8A',
    borderColor: '#5A6A8A',
    color: '#ffffff',
  },
  responseFormatHint: {
    fontSize: '11px',
    color: '#94a3b8',
    margin: 0,
    fontStyle: 'italic',
  },
  // Voice selector styles
  voiceSelectorContainer: {
    marginTop: '12px',
  },
  voiceSelectorLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '6px',
  },
  voiceSelectorSelect: {
    width: '100%',
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    fontSize: '13px',
    color: '#1e293b',
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  // Audio player styles
  audioPlayerContainer: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
  },
  audioPlayer: {
    width: '100%',
    height: '32px',
  },
  voiceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    backgroundColor: '#dbeafe',
    color: '#2563eb',
    fontSize: '10px',
    fontWeight: 500,
    borderRadius: '4px',
    marginBottom: '6px',
  },
  // Simulation styles
  simulationBadge: {
    marginLeft: '8px',
    padding: '2px 8px',
    backgroundColor: '#dbeafe',
    color: '#2563eb',
    fontSize: '10px',
    fontWeight: 500,
    borderRadius: '4px',
    textTransform: 'none',
    letterSpacing: 'normal',
  },
  simulationField: {
    marginBottom: '12px',
  },
  simulationLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '6px',
  },
  simulationTextarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '13px',
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
    lineHeight: '1.4',
    minHeight: '60px',
  },
  simulationInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  simulationButton: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  simulationButtonStart: {
    backgroundColor: '#22c55e',
    color: '#ffffff',
  },
  simulationButtonStop: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
  },
  simulationButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  // Session management styles
  sessionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e2e8f0',
  },
  sessionBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sessionBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sessionLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#64748b',
  },
  currentSessionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sessionName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
  },
  sessionBadge: {
    fontSize: '11px',
    padding: '2px 8px',
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    borderRadius: '4px',
    fontWeight: 500,
  },
  noSessionText: {
    fontSize: '14px',
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  sessionButton: {
    padding: '6px 12px',
    fontSize: '13px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    color: '#64748b',
    cursor: 'pointer',
    fontWeight: 500,
  },
  sessionButtonPrimary: {
    padding: '6px 12px',
    fontSize: '13px',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '6px',
    color: '#ffffff',
    cursor: 'pointer',
    fontWeight: 500,
  },
  createSessionForm: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sessionNameInput: {
    padding: '6px 12px',
    fontSize: '13px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    outline: 'none',
    width: '200px',
  },
  sessionList: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    marginBottom: '16px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  sessionListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  sessionListTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
  },
  showHiddenLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#64748b',
    cursor: 'pointer',
  },
  noSessions: {
    padding: '24px',
    textAlign: 'center',
    fontSize: '14px',
    color: '#94a3b8',
  },
  sessionItems: {
    display: 'flex',
    flexDirection: 'column',
  },
  sessionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  sessionItemActive: {
    backgroundColor: '#eff6ff',
    borderLeft: '3px solid #3b82f6',
  },
  sessionItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  sessionItemName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#1e293b',
  },
  sessionItemDate: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  archiveButton: {
    padding: '4px 10px',
    fontSize: '12px',
    backgroundColor: 'transparent',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    color: '#dc2626',
    cursor: 'pointer',
  },
  toolContextContent: {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#475569',
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
    maxHeight: '200px',
    overflowY: 'auto',
  },
  toolsBadge: {
    marginLeft: '8px',
    padding: '1px 6px',
    backgroundColor: '#dbeafe',
    color: '#2563eb',
    fontSize: '10px',
    borderRadius: '3px',
  },
  latencyBadge: {
    marginLeft: '8px',
    padding: '1px 6px',
    backgroundColor: '#fef3c7',
    color: '#d97706',
    fontSize: '10px',
    borderRadius: '3px',
    fontFamily: 'monospace',
  },
  messageTypeBadge: {
    marginLeft: '8px',
    padding: '1px 6px',
    backgroundColor: '#f3e8ff',
    color: '#9333ea',
    fontSize: '10px',
    borderRadius: '3px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  // Tool execution list styles (for last response)
  toolExecutionList: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  toolExecutionHeader: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#64748b',
    marginBottom: '8px',
  },
  toolExecutionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 8px',
    backgroundColor: '#f8fafc',
    borderRadius: '4px',
    marginBottom: '4px',
  },
  toolExecutionName: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#1e293b',
  },
  toolExecutionStatus: {
    fontSize: '11px',
    fontFamily: 'monospace',
    padding: '2px 6px',
    borderRadius: '3px',
  },
  toolExecutionSuccess: {
    backgroundColor: '#dcfce7',
    color: '#16a34a',
  },
  toolExecutionFailure: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
  },
  // Tool breakdown styles (for session analytics)
  toolBreakdownContainer: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  toolBreakdownTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#64748b',
    marginBottom: '8px',
  },
  toolBreakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  toolBreakdownName: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#1e293b',
  },
  toolBreakdownStats: {
    fontSize: '11px',
    fontFamily: 'monospace',
    color: '#64748b',
  },
  // Selected message bubble
  selectedBubble: {
    outline: '2px solid #3b82f6',
    outlineOffset: '2px',
  },
  // Message introspection styles
  closeIntrospectionButton: {
    marginLeft: 'auto',
    padding: '2px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#94a3b8',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  introspectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  introspectionRow: {
    display: 'flex',
    gap: '8px',
  },
  introspectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#64748b',
    minWidth: '60px',
  },
  introspectionValue: {
    fontSize: '12px',
    color: '#1e293b',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  introspectionContextTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#64748b',
    marginTop: '8px',
    marginBottom: '4px',
  },
  introspectionContentBox: {
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    padding: '8px',
    maxHeight: '150px',
    overflowY: 'auto',
  },
  introspectionContentPre: {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#475569',
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  // Input area wrapper for attachments preview
  inputAreaWrapper: {
    display: 'flex',
    flexDirection: 'column',
    borderTop: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  // Attach button styles
  attachButton: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    color: '#475569',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  // Pending attachments preview styles
  attachmentsPreviewContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  attachmentPreviewItem: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    width: '80px',
  },
  attachmentPreviewImage: {
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '4px',
  },
  attachmentPreviewDoc: {
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: '4px',
  },
  attachmentPreviewDocIcon: {
    fontSize: '24px',
  },
  attachmentPreviewName: {
    fontSize: '10px',
    color: '#64748b',
    marginTop: '4px',
    textAlign: 'center',
    wordBreak: 'break-all',
  },
  attachmentRemoveButton: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    width: '20px',
    height: '20px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    fontSize: '14px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  // Message attachments display styles
  messageAttachmentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '8px',
  },
  messageAttachmentItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  messageAttachmentImage: {
    maxWidth: '200px',
    maxHeight: '200px',
    borderRadius: '8px',
    objectFit: 'cover',
    cursor: 'pointer',
  },
  attachmentImageLink: {
    display: 'block',
  },
  messageAttachmentFile: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '6px',
    textDecoration: 'none',
    color: 'inherit',
  },
  userAttachmentFile: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  attachmentFileIcon: {
    fontSize: '20px',
  },
  attachmentFileName: {
    fontSize: '13px',
    fontWeight: 500,
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

// Add CSS animation for typing indicator and cursor blink
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes typing {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-4px); }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;
if (!document.head.querySelector('style[data-test-bot-animations]')) {
  styleSheet.setAttribute('data-test-bot-animations', 'true');
  document.head.appendChild(styleSheet);
}

export default TestBotPage;
